import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import EntityForm from "@/components/EntityForm";
import ClientDetailDialog from "@/components/ClientDetailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Building2, MoreVertical, Pencil, Trash2, Search, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Clients() {
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [params] = useSearchParams();
  const [detailId, setDetailId] = useState(params.get("id"));
  const [q, setQ] = useState("");

  const load = () => api.get("/clients").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const schema = [
    { name: "nom", label: "Nom / Raison sociale", required: true, col: 2 },
    { name: "type_client", label: "Type de client", type: "select", options: settings.client_types || [] },
    { name: "source", label: "Source", type: "select", options: settings.sources || [] },
    { name: "telephone", label: "Téléphone" },
    { name: "whatsapp", label: "WhatsApp" },
    { name: "email", label: "Email", type: "email" },
    { name: "pays", label: "Pays", type: "select", options: settings.countries || [] },
    { name: "ville", label: "Ville", type: "select", options: settings.cities || [] },
    { name: "adresse", label: "Adresse", col: 2 },
    { name: "nif", label: "NIF (Numéro d'identification fiscale)" },
    { name: "rccm", label: "RCCM" },
    { name: "notes", label: "Notes", type: "textarea" },
  ];

  const save = async (form) => {
    try {
      if (editing) { await api.put(`/clients/${editing.id}`, form); toast.success("Client mis à jour"); }
      else { await api.post("/clients", form); toast.success("Client créé"); }
      load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce client ?")) return; await api.delete(`/clients/${id}`); toast.success("Supprimé"); load(); };

  const filtered = (items || []).filter((c) =>
    !q || `${c.nom} ${c.telephone} ${c.email} ${c.ville}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Clients" subtitle="Portefeuille clients — fiches, historique & chiffre d'affaires">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-client" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau client</Button>
      </PageHeader>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer…" className="pl-9" data-testid="client-filter" />
      </div>

      {items === null ? <Loader /> : filtered.length === 0 ? (
        <EmptyState icon={Building2} title="Aucun client" subtitle="Créez un client ou convertissez une opportunité gagnée."
          action={<Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-brand-600 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau client</Button>} />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>N°</TableHead><TableHead>Nom / Raison sociale</TableHead><TableHead>Type</TableHead>
              <TableHead>Téléphone</TableHead><TableHead>Ville</TableHead><TableHead>Commercial</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetailId(c.id)}>
                  <TableCell className="font-mono text-xs text-slate-500">{c.numero}</TableCell>
                  <TableCell className="font-semibold">{c.nom}</TableCell>
                  <TableCell><StatusBadge value={c.type_client} /></TableCell>
                  <TableCell className="font-mono text-xs">{c.telephone || "—"}</TableCell>
                  <TableCell className="text-slate-600">{c.ville || "—"}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{c.owner_name}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`client-menu-${c.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setDetailId(c.id)} data-testid={`client-view-${c.id}`}><Eye className="h-4 w-4 mr-2" />Fiche client</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditing(c); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(c.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier le client" : "Nouveau client"}
        schema={schema} initial={editing} onSubmit={save} />
      <ClientDetailDialog clientId={detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}
